import { FC, useEffect, useMemo } from 'react'

import { useQueryClient, useSuspenseQuery } from '@tanstack/react-query'

import { useToaster } from '@forto-dev/phoenix-ui-core'

import { useAuth } from '@/features/auth'
import { socketTokenQueryOptions } from '@/features/feed'
import { error, info } from '@/utils/logger'
import { SOCKET_NAME_SPACE_REPORTS } from '@/utils/websocket/SocketService'
import { useSocket } from '@/utils/websocket/useSocket'

import { downloadReportDocument } from './reports.service'
import { useReportWebsocketContext } from './useReportWebsocketContext'
import { getLabelForReportType, Report, ReportTemplate, ReportType } from '../domain/report'

// we don't want to allow user to download reports sent by email
const REPORT_TYPES_SENT_BY_EMAIL: ReportType[] = ['emissions']

const useReportsSocket = () => {
  const { createDomainEventsListeners, initializeSocket, disconnectSocket } = useSocket()
  const { updateState } = useReportWebsocketContext()
  const { customer } = useAuth()
  const toaster = useToaster()
  const queryClient = useQueryClient()

  const initialiseReportsSocket = useMemo(
    () => (token: string) => {
      const setLink = async (res: {
        domainEventType: 'report-success-event'
        id: string // customerID or the id that the websocket has
        reportType: 'report-generate-event' // not sure what this is, I guess the type of event that triggered this response?
        payload: {
          commandType: 'report-generate-event'
        } & Report
      }) => {
        const { commandType, template, type, reportTemplateId, uploads } = res.payload
        info('report generated', { response: res })
        if (commandType === 'report-generate-event') {
          if (!REPORT_TYPES_SENT_BY_EMAIL.includes(type)) {
            await downloadReportDocument(uploads)
            toaster({
              title: `${getLabelForReportType(type)} "${template.name}" generated`, // TODO: translate
            })
            // Since it should keep saying it's pending if the report was requested, no reason for a user to re-request it on the same page
            updateState(reportTemplateId, false)
          } else {
            toaster({
              title: `Report "${template.name}" has been requested`, //'reports-request-emissions-success_message' // TODO: translate
            })
          }
        }
      }
      const updateSuccess = (res: {
        id: string // customerID or the id that the websocket has
        domainEventType: 'template-success-event'
        payload: {
          commandType: 'report-update-event' | 'report-remove-event' | 'report-create-event'
        } & ReportTemplate
        reportType: 'report-update-event' | 'report-remove-event' | 'report-create-event'
      }) => {
        let message = ''
        if (res.payload.commandType === 'report-update-event') {
          message = `"${res.payload.name}" has been updated.` // TODO: translate
        } else if (res.payload.commandType === 'report-remove-event') {
          message = `"${res.payload.name}" has been deleted.` // TODO: translate
        } else if (res.payload.commandType === 'report-create-event') {
          message = `"${res.payload.name}" has been created.` // TODO: translate
        }

        toaster({
          title: message,
        })
        void queryClient.invalidateQueries({
          queryKey: ['reports'],
        })
      }

      const handleError = (res: {
        domainEventType: 'report-internal-error-event'
        id: string // customerID or the id that the websocket has
        reportType: 'report-generate-event' // not sure what this is, I guess the type of event that triggered this response?
        payload: {
          commandType: 'report-generate-event'
          customerId: string
          errorData?: {
            code: string
            message: string
          }
        }
      }) => {
        // uiStore.snack.addSnack({ message: translate('reports-error_generic'), type: 'error' })
        // uiStore.loader.hide()
        error('could not download report', { response: res })
        toaster({
          title: `Could not download report`, // TODO: translate
          description: res.payload?.errorData?.message,
        })
        // TODO: how to update state without the report id in the response?
        // updateState(res.payload._id, false)
      }
      initializeSocket({
        token,
        nameSpace: SOCKET_NAME_SPACE_REPORTS,
        params: {
          customerId: customer?._id,
        },
      })
      createDomainEventsListeners([
        { eventType: 'report-success-event', callback: setLink as (args: unknown) => void },
        { eventType: 'report-internal-error-event', callback: handleError as (args: unknown) => void },
        {
          eventType: 'template-success-event',
          callback: updateSuccess as (args: unknown) => void,
        },
        {
          eventType: 'report-request-error-event',
          callback: handleError as (args: unknown) => void,
        },
      ])
    },
    [initializeSocket, customer?._id, createDomainEventsListeners, toaster, updateState, queryClient]
  )
  return { initialiseReportsSocket, disconnectSocket }
}

export const ReportsSocket: FC = () => {
  const { customer } = useAuth()

  const {
    data: { token },
  } = useSuspenseQuery(
    socketTokenQueryOptions(
      {
        type: 'customer',
        referenceId: customer?._id ?? '',
      },
      !!customer?._id
    )
  )
  const { initialiseReportsSocket, disconnectSocket } = useReportsSocket()
  useEffect(() => {
    if (token) {
      initialiseReportsSocket(token)
    }

    return () => {
      disconnectSocket()
    }
  }, [disconnectSocket, initialiseReportsSocket, token])
  return null
}
